/**
 * Craftin domain types.
 * Conventions: money as integer cents + EUR; rates in bps where applicable;
 * ISO 8601 dates for value/due dates; server timestamps for audit fields.
 * See docs/architecture.md.
 */

import type { Timestamp } from 'firebase/firestore';

/** `YYYY-MM-DD` */
export type IsoDateString = string;

/** Canonical money — minor units (EUR cents). Never floats. */
export type Money = {
  amountCents: number;
  currency: 'EUR';
};

export function moneyEUR(amountCents: number): Money {
  return { amountCents, currency: 'EUR' };
}

// ---------------------------------------------------------------------------
// Platform & company membership
// ---------------------------------------------------------------------------

/**
 * The single role key. Stored on `users/{uid}.role` — decides everything.
 * - internal_admin: Craftin staff, full access across all organizations.
 * - client_admin: full access inside their own organization.
 * - client_user: operational work (customers, documents, cases) inside their org.
 * - lender_admin: lender staff — reads their lender catalog entry and assigned cases.
 */
export type UserRole = 'internal_admin' | 'client_admin' | 'client_user' | 'lender_admin';

export const USER_ROLES: UserRole[] = [
  'internal_admin',
  'client_admin',
  'client_user',
  'lender_admin',
];

/** Roles a client organization can assign to its members (invites, member management). */
export type ClientRole = 'client_admin' | 'client_user';
export const CLIENT_ROLES: ClientRole[] = ['client_admin', 'client_user'];

export type CompanyStatus = 'draft' | 'onboarding' | 'active' | 'suspended' | 'archived';

export type LegalForm =
  | 'GmbH'
  | 'GmbH & Co. KG'
  | 'AG'
  | 'UG (haftungsbeschränkt)'
  | 'KG'
  | 'OHG'
  | 'e.K.'
  | 'Sonstige';

export const LEGAL_FORMS: LegalForm[] = [
  'GmbH',
  'GmbH & Co. KG',
  'AG',
  'UG (haftungsbeschränkt)',
  'KG',
  'OHG',
  'e.K.',
  'Sonstige',
];

/**
 * Company (tenant). Stored under `organizations/{orgId}` for path/rules compatibility.
 * `id` === orgId; UI/docs say Company.
 */
export interface Company {
  id: string;
  /** Display name (trading or legal short). */
  name: string;
  /** Lowercased/normalized `name` — lead-widget lookup key (see leadIntake). */
  nameNormalized?: string;
  status: CompanyStatus;
  legalName: string;
  tradingName?: string;
  legalForm: LegalForm | string;
  registrationNumber?: string;
  registrationCourt?: string;
  vatId?: string;
  taxNumber?: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  countryCode: 'DE';
  email: string;
  phone?: string;
  website?: string;
  industry?: string;
  tradeCategories?: string[];
  onboardingStatus: string;
  locale?: 'de' | 'en';
  bookingFlowSettingsId?: string;
  brandingId?: string;
  createdBy: string;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
  updatedBy?: string;
  archivedAt?: Timestamp | null;
  version?: number;
}

/** Alias — OrgContext / legacy imports. */
export type Organization = Company;

/** Pure membership marker — who belongs to the org. The role lives on `users/{uid}.role`. */
export interface Membership {
  uid: string;
  orgId: string;
  email: string;
  displayName?: string;
  invitationId?: string;
  createdAt: Timestamp | null;
}

export type InvitationStatus = 'pending' | 'accepted' | 'declined';

export interface Invitation {
  id: string;
  orgId: string;
  orgName: string;
  email: string;
  role: ClientRole;
  status: InvitationStatus;
  invitedBy: string;
  invitedByName?: string;
  /** Token-based invites (Cloud Function `sendInvitation`) expire. */
  expiresAt?: Timestamp | null;
  emailStatus?: 'sent' | 'failed';
  createdAt: Timestamp | null;
}

// ---------------------------------------------------------------------------
// Branding & checkout configuration
// ---------------------------------------------------------------------------

export interface CompanyBranding {
  id: string;
  companyId: string;
  logoUrl?: string;
  faviconUrl?: string;
  /** Hero/background image for white-label public pages (`/check?org=…`). */
  heroImageUrl?: string;
  primaryColor: string;
  secondaryColor?: string;
  accentColor?: string;
  supportEmail?: string;
  supportPhone?: string;
  legalNoticeUrl?: string;
  privacyPolicyUrl?: string;
  termsUrl?: string;
  footerText?: string;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
}

export type CheckoutConfigurationStatus = 'draft' | 'active';

export interface CheckoutConfiguration {
  id: string;
  companyId: string;
  status: CheckoutConfigurationStatus;
  welcomeTitle?: string;
  welcomeDescription?: string;
  requirePhoneVerification: boolean;
  requireEmailVerification: boolean;
  allowCustomerPlanSelection: boolean;
  allowCustomerDocumentUpload: boolean;
  showCompanyContact: boolean;
  showLineItems: boolean;
  showTaxBreakdown: boolean;
  defaultExpirationDays: number;
  enabledInstallmentOptionIds: string[];
  consentConfigurationId?: string;
  successRedirectUrl?: string;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
}

// ---------------------------------------------------------------------------
// Customers
// ---------------------------------------------------------------------------

export type CustomerType = 'consumer' | 'business';
export type CustomerStatus = 'lead' | 'active' | 'blocked' | 'archived';

export interface Customer {
  id: string;
  companyId: string;
  type: CustomerType;
  firstName?: string;
  lastName?: string;
  businessName?: string;
  email?: string;
  phone?: string;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  countryCode?: string;
  dateOfBirth?: IsoDateString;
  externalReference?: string;
  status: CustomerStatus;
  createdAt: Timestamp | null;
  createdBy?: string;
  updatedAt: Timestamp | null;
  updatedBy?: string;
  archivedAt?: Timestamp | null;
  version?: number;
}

export type CustomerSnapshot = Pick<
  Customer,
  | 'id'
  | 'type'
  | 'firstName'
  | 'lastName'
  | 'businessName'
  | 'email'
  | 'phone'
  | 'street'
  | 'houseNumber'
  | 'postalCode'
  | 'city'
  | 'countryCode'
>;

// ---------------------------------------------------------------------------
// Commercial documents
// ---------------------------------------------------------------------------

export type CommercialDocumentType =
  | 'quote'
  | 'order_confirmation'
  | 'invoice'
  | 'partial_invoice'
  | 'final_invoice';

export type CommercialDocumentSource = 'upload' | 'manual' | 'api';

export type ExtractionStatus =
  | 'not_required'
  | 'queued'
  | 'processing'
  | 'needs_review'
  | 'completed'
  | 'failed';

export type CommercialDocumentStatus = 'draft' | 'reviewed' | 'final' | 'void' | 'archived';

export interface CommercialDocumentLineItem {
  id: string;
  position?: number;
  description: string;
  quantity: number;
  unit?: string;
  unitPriceNetCents: number;
  vatRateBps: number;
  lineNetCents: number;
  lineVatCents: number;
  lineGrossCents: number;
  confidence?: number;
}

export interface TaxBreakdown {
  vatRateBps: number;
  netCents: number;
  vatCents: number;
}

export interface CompanySnapshot {
  id: string;
  legalName: string;
  tradingName?: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  countryCode: string;
  email: string;
  vatId?: string;
}

export type CompanyPublicSnapshot = Pick<
  CompanySnapshot,
  'id' | 'legalName' | 'tradingName' | 'city' | 'email'
> & { supportEmail?: string; supportPhone?: string };

export interface CommercialDocument {
  id: string;
  companyId: string;
  type: CommercialDocumentType;
  source: CommercialDocumentSource;
  originalFileId?: string;
  storagePath?: string;
  documentNumber?: string;
  issueDate?: IsoDateString;
  serviceDate?: IsoDateString;
  validUntil?: IsoDateString;
  dueDate?: IsoDateString;
  customerId?: string;
  customerSnapshot: CustomerSnapshot;
  companySnapshot: CompanySnapshot;
  lineItems: CommercialDocumentLineItem[];
  subtotalNetCents: number;
  taxBreakdown: TaxBreakdown[];
  totalTaxCents: number;
  totalGrossCents: number;
  currency: 'EUR';
  paymentTerms?: string;
  notes?: string;
  extractionStatus: ExtractionStatus;
  extractionConfidence?: number;
  extractionVersion?: string;
  status: CommercialDocumentStatus;
  createdAt: Timestamp | null;
  createdBy: string;
  updatedAt: Timestamp | null;
  updatedBy?: string;
  archivedAt?: Timestamp | null;
  version?: number;
}

export type CommercialDocumentSnapshot = Omit<
  CommercialDocument,
  'createdAt' | 'updatedAt' | 'archivedAt' | 'extractionStatus'
> & {
  snapshottedAt: string;
};

// ---------------------------------------------------------------------------
// Lenders & installment options
// ---------------------------------------------------------------------------

export type LenderStatus = 'sandbox' | 'active' | 'inactive';

export interface Lender {
  id: string;
  name: string;
  status: LenderStatus;
  integrationType: string;
  supportedProducts: string[];
  supportedCountries: string[];
  supportedCustomerTypes: CustomerType[];
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
}

export type InstallmentOptionStatus = 'draft' | 'active' | 'inactive' | 'archived';
export type InstallmentOptionScope = 'global' | 'company';

export interface InstallmentOption {
  id: string;
  scope: InstallmentOptionScope;
  companyId?: string;
  lenderId: string;
  lenderProductId?: string;
  name: string;
  description?: string;
  status: InstallmentOptionStatus;
  termMonths: number;
  /** Nominal interest rate in basis points (e.g. 599 = 5.99%). */
  nominalInterestRateBps: number;
  effectiveAnnualRateBps?: number;
  setupFeeCents?: number;
  monthlyFeeCents?: number;
  merchantFeePercentBps?: number;
  merchantFeeFixedCents?: number;
  minFinancedAmountCents: number;
  maxFinancedAmountCents: number;
  minDownPaymentCents?: number;
  maxDownPaymentCents?: number;
  firstPaymentDelayDays?: number;
  allowedCustomerTypes: CustomerType[];
  allowedCountries: string[];
  calculationVersion: string;
  validFrom?: Timestamp | null;
  validUntil?: Timestamp | null;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
  archivedAt?: Timestamp | null;
}

export interface PaymentScheduleEntry {
  installmentNumber: number;
  dueDate?: IsoDateString;
  principalCents: number;
  interestCents: number;
  feeCents: number;
  totalCents: number;
}

export interface FinancingCalculationSnapshot {
  calculationVersion: string;
  financedAmountCents: number;
  downPaymentCents: number;
  termMonths: number;
  nominalInterestRateBps: number;
  monthlyInstallmentCents: number;
  finalInstallmentCents: number;
  totalInterestCents: number;
  totalFeesCents: number;
  totalAmountPayableCents: number;
  effectiveAnnualRateBps?: number;
  merchantCostCents: number;
  schedule: PaymentScheduleEntry[];
  lenderId: string;
  installmentOptionId: string;
}

// ---------------------------------------------------------------------------
// Financing case & application
// ---------------------------------------------------------------------------

export type FinancingCaseStatus =
  | 'draft'
  | 'ready'
  | 'sent'
  | 'opened'
  | 'in_progress'
  | 'submitted'
  | 'under_review'
  | 'approved'
  | 'rejected'
  | 'accepted'
  | 'contracted'
  | 'payout_pending'
  | 'paid_out'
  | 'completed'
  | 'cancelled'
  | 'expired';

export interface FinancingCase {
  id: string;
  companyId: string;
  customerId: string;
  commercialDocumentId: string;
  caseNumber: string;
  title?: string;
  description?: string;
  amountCents: number;
  currency: 'EUR';
  downPaymentCents?: number;
  allowedInstallmentOptionIds: string[];
  selectedInstallmentOptionId?: string;
  /** SHA-256 hex of the public token — never store the raw token. */
  publicTokenHash: string;
  publicFlowVersion: number;
  expiresAt: Timestamp | null;
  revokedAt?: Timestamp | null;
  status: FinancingCaseStatus;
  /** How the case entered the system; `lead` = public financing-check widget. */
  source?: 'wizard' | 'lead';
  /** Client-generated key deduplicating lead-widget retries. */
  idempotencyKey?: string;
  customerSnapshot: CustomerSnapshot;
  documentSnapshot: CommercialDocumentSnapshot;
  companySnapshot: CompanyPublicSnapshot;
  calculationSnapshot?: FinancingCalculationSnapshot;
  sentAt?: Timestamp | null;
  firstOpenedAt?: Timestamp | null;
  submittedAt?: Timestamp | null;
  completedAt?: Timestamp | null;
  createdAt: Timestamp | null;
  createdBy: string;
  updatedAt: Timestamp | null;
  updatedBy?: string;
  archivedAt?: Timestamp | null;
  version?: number;
}

export type FinancingApplicationStatus =
  | 'draft'
  | 'submitted'
  | 'processing'
  | 'additional_information_required'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'expired'
  | 'error';

export interface ApplicantSnapshot {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  dateOfBirth?: IsoDateString;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  countryCode?: string;
}

export interface FinancingApplication {
  id: string;
  companyId: string;
  financingCaseId: string;
  customerId: string;
  lenderId: string;
  installmentOptionId: string;
  status: FinancingApplicationStatus;
  financedAmountCents: number;
  downPaymentCents?: number;
  currency: 'EUR';
  calculationSnapshot: FinancingCalculationSnapshot;
  applicantSnapshot: ApplicantSnapshot;
  consentRecordIds: string[];
  documentIds: string[];
  externalApplicationId?: string;
  externalStatus?: string;
  idempotencyKey?: string;
  submittedAt?: Timestamp | null;
  decisionAt?: Timestamp | null;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
  version?: number;
}

export interface ConsentDefinition {
  id: string;
  key: string;
  locale: 'de' | 'en';
  version: string;
  title: string;
  body: string;
  required: boolean;
  createdAt: Timestamp | null;
}

export interface ConsentRecord {
  id: string;
  companyId: string;
  financingCaseId: string;
  financingApplicationId?: string;
  consentDefinitionId: string;
  consentKey: string;
  textVersion: string;
  locale: 'de' | 'en';
  accepted: true;
  acceptedAt: Timestamp | null;
  sourceIp?: string;
  userAgent?: string;
  createdAt: Timestamp | null;
}

export interface PublicCheckoutTokenDoc {
  tokenHash: string;
  orgId: string;
  caseId: string;
  expiresAt: Timestamp | null;
  revokedAt?: Timestamp | null;
  createdAt: Timestamp | null;
}

// ---------------------------------------------------------------------------
// Files & processing jobs
// ---------------------------------------------------------------------------

export type FileUploadStatus = 'pending' | 'uploaded' | 'failed';
export type FileProcessingStatus = 'none' | 'queued' | 'processing' | 'done' | 'failed';

export interface StoredFile {
  id: string;
  companyId: string;
  storagePath: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  checksum?: string;
  uploadStatus: FileUploadStatus;
  processingStatus: FileProcessingStatus;
  entityType?: string;
  entityId?: string;
  createdAt: Timestamp | null;
  createdBy: string;
}

export type DocumentProcessingJobStatus =
  | 'queued'
  | 'ocr'
  | 'extracting'
  | 'validating'
  | 'needs_review'
  | 'completed'
  | 'failed';

export interface DocumentProcessingJob {
  id: string;
  companyId: string;
  commercialDocumentId: string;
  status: DocumentProcessingJobStatus;
  errorCode?: string;
  errorMessage?: string;
  extractionVersion?: string;
  correlationId?: string;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
}

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------

export type AuditActorType = 'internal_user' | 'client_user' | 'customer' | 'system' | 'lender';

export interface AuditEvent {
  id: string;
  orgId: string;
  companyId?: string;
  actorUid: string;
  actorRole: string;
  actorType?: AuditActorType;
  action: string;
  objectType: string;
  objectId: string;
  entityType?: string;
  entityId?: string;
  approverUid?: string | null;
  before?: unknown;
  after?: unknown;
  previousValues?: unknown;
  changedValues?: unknown;
  correlationId?: string;
  at: Timestamp | null;
  createdAt?: Timestamp | null;
}
